import React from "react"
import { Navigate, useSearchParams } from "react-router-dom"
import { useSignal } from "@preact/signals-react"
import { Alert, AlertTitle, Box, CircularProgress, Typography } from "@mui/material"
import { alpha } from "@mui/material/styles"
import { ButtonLoadingSeeker, IconSeeker, PasswordFieldSeeker, TextFieldSeeker } from "@ui-seeker/core"

import { HOME_ROUTES } from "@constants"
import { useUserApp } from "@hooks"
import { dsColorNew } from "@palette"
import LoginPriceCurves from "./@components/PriceCurves"


const MODULES = [
	{ icon: 'magnifying-glass-dollar', label: 'Rate shopping' },
	{ icon: 'chart-line', label: 'Analyzers' },
	{ icon: 'bell', label: 'Alerts' },
	{ icon: 'wand-magic-sparkles', label: 'Repricing' },
]

const greeting = () => {
	const hour = new Date().getHours()
	return hour < 12 ? 'Good morning' : hour < 20 ? 'Good afternoon' : 'Good evening'
}

const whiteAlpha = value => alpha(dsColorNew.white, value)


export default function LoginPage() {
	const [searchParams] = useSearchParams()
	const { isLogged, checkingLogging, isLoadingLogin, handleLogin, error } = useUserApp(true)
	const username = useSignal('')
	const password = useSignal('')

	const redirect = searchParams.get('redirect')

	if (isLogged) {
		if (redirect) return <Navigate to={redirect} replace/>
		else return <Navigate to={HOME_ROUTES.HOME_PATH} replace/>
	}

	const handleSubmit = e => {
		e.preventDefault()
		handleLogin(username.value, password.value)
	}

	return <Box sx={{ display: 'flex', height: '100vh' }}>

		{/* Brand panel: competitor price lines drifting behind the brand */}
		<Box sx={{
			flex: 65,
			position: 'relative',
			overflow: 'hidden',
			background: `linear-gradient(135deg, ${dsColorNew.webBlueHover} 0%, ${dsColorNew.navy} 100%)`,
		}}>
			<LoginPriceCurves/>

			<Box sx={{
				position: 'relative', zIndex: 1, height: 1,
				display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
				gap: 3.5, p: 8, textAlign: 'center',
			}}>
				{/* Logo placeholder until the light logo for dark backgrounds exists */}
				<Box sx={{
					width: 80, height: 80, borderRadius: '50%',
					backgroundColor: whiteAlpha(0.12),
					backdropFilter: 'blur(8px)',
					border: `1px solid ${whiteAlpha(0.18)}`,
					display: 'flex', alignItems: 'center', justifyContent: 'center',
				}}>
					<IconSeeker icon="car" sx={{ fontSize: 34, color: whiteAlpha(0.92) }}/>
				</Box>

				<Typography sx={{ fontSize: 42, fontWeight: 700, color: dsColorNew.white, letterSpacing: '-0.5px' }}>
					DS Rentacar
				</Typography>

				<Typography sx={{ fontSize: 18, color: whiteAlpha(0.75), maxWidth: 440 }}>
					Know every rate before your competitors do
				</Typography>

				<Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 1.25, mt: 1 }}>
					{MODULES.map(module => <Box key={module.label} sx={{
						display: 'inline-flex', alignItems: 'center', gap: 0.75,
						px: 1.75, py: 0.875, borderRadius: '999px',
						backgroundColor: whiteAlpha(0.07),
						border: `1px solid ${whiteAlpha(0.12)}`,
						backdropFilter: 'blur(4px)',
					}}>
						<IconSeeker icon={module.icon} sx={{ fontSize: 13, color: dsColorNew.cyan }}/>
						<Typography sx={{ fontSize: 13, color: whiteAlpha(0.82) }}>{module.label}</Typography>
					</Box>)}
				</Box>
			</Box>
		</Box>

		{/* Form panel */}
		<Box sx={{
			flex: 35, display: 'flex', flexDirection: 'column',
			alignItems: 'center', justifyContent: 'center',
			backgroundColor: dsColorNew.loginFormBg, p: 8,
		}}>
			<Box
				component="form"
				onSubmit={handleSubmit}
				sx={{
					width: '100%', maxWidth: 460,
					backgroundColor: dsColorNew.white,
					borderRadius: '20px',
					borderTop: `3px solid ${dsColorNew.webBlue500}`,
					boxShadow: `0 2px 4px ${alpha(dsColorNew.navy, 0.04)}, 0 12px 40px ${alpha(dsColorNew.navy, 0.10)}`,
					p: 6,
				}}
			>
				<Typography sx={{ fontSize: 28, fontWeight: 700, color: dsColorNew.textTitle, mb: 0.5 }}>
					{greeting()}
				</Typography>
				<Typography sx={{ fontSize: 15, color: dsColorNew.textMuted, mb: 4 }}>
					Sign in to your account to continue
				</Typography>

				{!!checkingLogging && <Box sx={{ textAlign: 'center', py: 4 }}>
					<Typography sx={{ mb: 3, fontSize: 16, color: dsColorNew.textMuted }}>
						Checking User Info
					</Typography>
					<CircularProgress size={60} sx={{ color: dsColorNew.appBlue }}/>
				</Box>}

				{!checkingLogging && <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>

					{!!error && <Alert severity="error">
						<AlertTitle>Login Error</AlertTitle>
						{error}
					</Alert>}

					<Box>
						<Typography sx={{ fontSize: 14, fontWeight: 500, color: dsColorNew.textTitle, mb: 0.75 }}>
							User Name
						</Typography>
						<TextFieldSeeker
							placeholder="Enter your user name"
							value={username.value}
							onChange={e => username.value = e.target.value}
							startIcon="user"
						/>
					</Box>

					<Box>
						<Typography sx={{ fontSize: 14, fontWeight: 500, color: dsColorNew.textTitle, mb: 0.75 }}>
							Password
						</Typography>
						<PasswordFieldSeeker
							label=""
							value={password.value}
							onChange={e => password.value = e.target.value}
							startIcon="lock"
						/>
					</Box>

					<ButtonLoadingSeeker
						type="submit"
						loading={isLoadingLogin}
						sx={{
							background: `linear-gradient(135deg, ${dsColorNew.webBlue500}, ${dsColorNew.cyan})`,
							color: dsColorNew.white,
							borderRadius: '8px',
							height: 48,
							fontSize: 16,
							fontWeight: 600,
							textTransform: 'none',
							mt: 2,
							'&:hover': { background: `linear-gradient(135deg, ${dsColorNew.webBlueHover}, ${dsColorNew.cyanHover})` }
						}}
					>
						Sign In →
					</ButtonLoadingSeeker>
				</Box>}
			</Box>
		</Box>

	</Box>
}
